import { supabase } from '@/integrations/supabase/client';

export interface TagCategory {
  id: string;
  name: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TagSubcategory {
  id: string;
  category_id: string;
  name: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TagGroup {
  id: string;
  subcategory_id: string;
  name: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface EnhancedTag {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  color: string;
  category_id?: string;
  subcategory_id?: string;
  group_id?: string;
  is_system: boolean;
  is_active: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface TagHierarchy {
  tag_id: string;
  tag_name: string;
  tag_description?: string;
  tag_color: string;
  tag_is_system: boolean;
  usage_count: number;
  tag_created_at: string;
  group_id?: string;
  group_name?: string;
  group_description?: string;
  group_is_system?: boolean;
  subcategory_id?: string;
  subcategory_name?: string;
  subcategory_description?: string;
  subcategory_is_system?: boolean;
  category_id?: string;
  category_name?: string;
  category_description?: string;
  category_is_system?: boolean;
  full_path: string;
}

export interface TagSearchFilters {
  search_term?: string;
  category_id?: string;
  subcategory_id?: string;
  group_id?: string;
  include_inactive?: boolean;
}

export interface CustomTagData {
  name: string;
  description?: string;
  color?: string;
  category_id?: string;
  subcategory_id?: string;
  group_id?: string;
}

export interface CustomCategoryData {
  name: string;
  description?: string;
}

export interface CustomSubcategoryData {
  name: string;
  description?: string;
  category_id: string;
}

export interface CustomGroupData {
  name: string;
  description?: string;
  subcategory_id: string;
}

class EnhancedTagService {
  private static instance: EnhancedTagService;
  private cache: Map<string, any> = new Map();
  private cacheExpiry = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): EnhancedTagService {
    if (!EnhancedTagService.instance) {
      EnhancedTagService.instance = new EnhancedTagService();
    }
    return EnhancedTagService.instance;
  }

  private getCached(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  private setCached(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  private clearCache(): void {
    this.cache.clear();
  }

  // Get all tag categories
  async getTagCategories(): Promise<TagCategory[]> {
    const cacheKey = 'tag_categories';
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('tag_categories')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching tag categories:', error);
      return [];
    }
  }

  // Get subcategories for a specific category
  async getTagSubcategories(categoryId: string): Promise<TagSubcategory[]> {
    const cacheKey = `tag_subcategories_${categoryId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('tag_subcategories')
        .select('*')
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching tag subcategories:', error);
      return [];
    }
  }

  // Get groups for a specific subcategory
  async getTagGroups(subcategoryId: string): Promise<TagGroup[]> {
    const cacheKey = `tag_groups_${subcategoryId}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('tag_groups')
        .select('*')
        .eq('subcategory_id', subcategoryId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching tag groups:', error);
      return [];
    }
  }

  // Get all tags with hierarchy information
  async getTagsWithHierarchy(filters: TagSearchFilters = {}): Promise<TagHierarchy[]> {
    const cacheKey = `tags_hierarchy_${JSON.stringify(filters)}`;
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    try {
      let query = supabase
        .from('tag_hierarchy')
        .select('*')
        .order('usage_count', { ascending: false })
        .order('tag_name');

      // Apply filters
      if (filters.category_id) {
        query = query.eq('category_id', filters.category_id);
      }
      if (filters.subcategory_id) {
        query = query.eq('subcategory_id', filters.subcategory_id);
      }
      if (filters.group_id) {
        query = query.eq('group_id', filters.group_id);
      }
      if (filters.search_term) {
        query = query.or(`tag_name.ilike.%${filters.search_term}%,tag_description.ilike.%${filters.search_term}%,full_path.ilike.%${filters.search_term}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      this.setCached(cacheKey, data);
      return data || [];
    } catch (error) {
      console.error('Error fetching tags with hierarchy:', error);
      return [];
    }
  }

  // Get user's custom tags
  async getUserTags(userId: string): Promise<EnhancedTag[]> {
    try {
      const { data, error } = await supabase
        .from('enhanced_tags')
        .select('*')
        .eq('user_id', userId)
        .eq('is_system', false)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user tags:', error);
      return [];
    }
  }

  // Search tags with full-text search
  async searchTags(searchTerm: string, filters: TagSearchFilters = {}): Promise<TagHierarchy[]> {
    try {
      const allTags = await this.getTagsWithHierarchy({
        ...filters,
        search_term: searchTerm
      });

      // Additional client-side filtering for better search results
      return allTags.filter(tag => 
        tag.tag_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tag.tag_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tag.full_path.toLowerCase().includes(searchTerm.toLowerCase())
      );
    } catch (error) {
      console.error('Error searching tags:', error);
      return [];
    }
  }

  // Create custom tag category
  async createCustomCategory(userId: string, categoryData: CustomCategoryData): Promise<string> {
    try {
      const { data, error } = await supabase
        .rpc('add_custom_tag_category', {
          p_user_id: userId,
          p_name: categoryData.name,
          p_description: categoryData.description
        });

      if (error) throw error;

      this.clearCache(); // Clear cache to refresh data
      return data;
    } catch (error) {
      console.error('Error creating custom tag category:', error);
      throw error;
    }
  }

  // Create custom tag subcategory
  async createCustomSubcategory(userId: string, subcategoryData: CustomSubcategoryData): Promise<string> {
    try {
      const { data, error } = await supabase
        .rpc('add_custom_tag_subcategory', {
          p_user_id: userId,
          p_category_id: subcategoryData.category_id,
          p_name: subcategoryData.name,
          p_description: subcategoryData.description
        });

      if (error) throw error;

      this.clearCache(); // Clear cache to refresh data
      return data;
    } catch (error) {
      console.error('Error creating custom tag subcategory:', error);
      throw error;
    }
  }

  // Create custom tag group
  async createCustomGroup(userId: string, groupData: CustomGroupData): Promise<string> {
    try {
      const { data, error } = await supabase
        .rpc('add_custom_tag_group', {
          p_user_id: userId,
          p_subcategory_id: groupData.subcategory_id,
          p_name: groupData.name,
          p_description: groupData.description
        });

      if (error) throw error;

      this.clearCache(); // Clear cache to refresh data
      return data;
    } catch (error) {
      console.error('Error creating custom tag group:', error);
      throw error;
    }
  }

  // Create custom tag
  async createCustomTag(userId: string, tagData: CustomTagData): Promise<string> {
    try {
      const { data, error } = await supabase
        .rpc('add_custom_tag', {
          p_user_id: userId,
          p_name: tagData.name,
          p_description: tagData.description,
          p_color: tagData.color || '#3B82F6',
          p_category_id: tagData.category_id,
          p_subcategory_id: tagData.subcategory_id,
          p_group_id: tagData.group_id
        });

      if (error) throw error;

      this.clearCache(); // Clear cache to refresh data
      return data;
    } catch (error) {
      console.error('Error creating custom tag:', error);
      throw error;
    }
  }

  // Update tag usage count
  async incrementTagUsage(tagId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('enhanced_tags')
        .update({ usage_count: supabase.raw('usage_count + 1') })
        .eq('id', tagId);

      if (error) throw error;
      this.clearCache(); // Clear cache to refresh data
    } catch (error) {
      console.error('Error incrementing tag usage:', error);
    }
  }

  // Get tag statistics
  async getTagStatistics(): Promise<{
    total_tags: number;
    system_tags: number;
    custom_tags: number;
    categories_count: number;
    subcategories_count: number;
    groups_count: number;
  }> {
    try {
      const [
        { count: total_tags },
        { count: system_tags },
        { count: custom_tags },
        { count: categories_count },
        { count: subcategories_count },
        { count: groups_count }
      ] = await Promise.all([
        supabase.from('enhanced_tags').select('*', { count: 'exact', head: true }),
        supabase.from('enhanced_tags').select('*', { count: 'exact', head: true }).eq('is_system', true),
        supabase.from('enhanced_tags').select('*', { count: 'exact', head: true }).eq('is_system', false),
        supabase.from('tag_categories').select('*', { count: 'exact', head: true }),
        supabase.from('tag_subcategories').select('*', { count: 'exact', head: true }),
        supabase.from('tag_groups').select('*', { count: 'exact', head: true })
      ]);

      return {
        total_tags: total_tags || 0,
        system_tags: system_tags || 0,
        custom_tags: custom_tags || 0,
        categories_count: categories_count || 0,
        subcategories_count: subcategories_count || 0,
        groups_count: groups_count || 0
      };
    } catch (error) {
      console.error('Error fetching tag statistics:', error);
      return {
        total_tags: 0,
        system_tags: 0,
        custom_tags: 0,
        categories_count: 0,
        subcategories_count: 0,
        groups_count: 0
      };
    }
  }

  // Get popular tags (most used)
  async getPopularTags(limit: number = 10): Promise<TagHierarchy[]> {
    try {
      const { data, error } = await supabase
        .from('tag_hierarchy')
        .select('*')
        .order('usage_count', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching popular tags:', error);
      return [];
    }
  }

  // Get tags by emission scope
  async getTagsByScope(scope: 'Scope 1' | 'Scope 2' | 'Scope 3'): Promise<TagHierarchy[]> {
    try {
      const { data, error } = await supabase
        .from('tag_hierarchy')
        .select('*')
        .ilike('category_name', `%${scope}%`)
        .order('tag_name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching tags by scope:', error);
      return [];
    }
  }

  // Get complete tag tree structure
  async getTagTree(): Promise<{
    categories: (TagCategory & {
      subcategories: (TagSubcategory & {
        groups: (TagGroup & {
          tags: EnhancedTag[];
        })[];
      })[];
    })[];
  }> {
    try {
      const categories = await this.getTagCategories();
      const tree = [];

      for (const category of categories) {
        const subcategories = await this.getTagSubcategories(category.id);
        const categoryWithSubs = {
          ...category,
          subcategories: []
        };

        for (const subcategory of subcategories) {
          const groups = await this.getTagGroups(subcategory.id);
          const subcategoryWithGroups = {
            ...subcategory,
            groups: []
          };

          for (const group of groups) {
            const { data: tags } = await supabase
              .from('enhanced_tags')
              .select('*')
              .eq('group_id', group.id)
              .eq('is_active', true)
              .order('name');

            subcategoryWithGroups.groups.push({
              ...group,
              tags: tags || []
            });
          }

          categoryWithSubs.subcategories.push(subcategoryWithGroups);
        }

        tree.push(categoryWithSubs);
      }

      return { categories: tree };
    } catch (error) {
      console.error('Error building tag tree:', error);
      return { categories: [] };
    }
  }

  // Bulk create tags from template
  async bulkCreateTagsFromTemplate(userId: string, template: {
    category: string;
    subcategory: string;
    group: string;
    tags: string[];
  }): Promise<string[]> {
    try {
      const tagIds: string[] = [];
      
      for (const tagName of template.tags) {
        const tagId = await this.createCustomTag(userId, {
          name: tagName,
          description: `${tagName} for ${template.group}`,
          color: this.generateRandomColor()
        });
        tagIds.push(tagId);
      }

      return tagIds;
    } catch (error) {
      console.error('Error bulk creating tags from template:', error);
      throw error;
    }
  }

  // Generate random color for tags
  private generateRandomColor(): string {
    const colors = [
      '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
      '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
      '#A855F7', '#F43F5E', '#0EA5E9', '#22C55E', '#EAB308'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  }

  // Export tags to CSV
  async exportTagsToCSV(filters: TagSearchFilters = {}): Promise<string> {
    try {
      const tags = await this.getTagsWithHierarchy(filters);
      
      const headers = [
        'Tag Name',
        'Description',
        'Color',
        'Category',
        'Subcategory',
        'Group',
        'Full Path',
        'Usage Count',
        'Is System Tag',
        'Created At'
      ];

      const csvRows = [
        headers.join(','),
        ...tags.map(tag => [
          `"${tag.tag_name}"`,
          `"${tag.tag_description || ''}"`,
          tag.tag_color,
          `"${tag.category_name || ''}"`,
          `"${tag.subcategory_name || ''}"`,
          `"${tag.group_name || ''}"`,
          `"${tag.full_path}"`,
          tag.usage_count,
          tag.tag_is_system,
          tag.tag_created_at
        ].join(','))
      ];

      return csvRows.join('\n');
    } catch (error) {
      console.error('Error exporting tags to CSV:', error);
      throw error;
    }
  }

  // Import tags from CSV
  async importTagsFromCSV(userId: string, csvContent: string): Promise<{
    success: number;
    errors: string[];
  }> {
    try {
      const lines = csvContent.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const dataRows = lines.slice(1);
      
      let success = 0;
      const errors: string[] = [];

      for (let i = 0; i < dataRows.length; i++) {
        try {
          const values = dataRows[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const rowData: any = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index] || '';
          });

          // Create tag if it doesn't exist
          const existingTags = await this.searchTags(rowData['Tag Name']);
          if (existingTags.length === 0) {
            await this.createCustomTag(userId, {
              name: rowData['Tag Name'],
              description: rowData['Description'],
              color: rowData['Color'] || this.generateRandomColor()
            });
            success++;
          }
        } catch (rowError) {
          errors.push(`Row ${i + 2}: ${rowError instanceof Error ? rowError.message : 'Unknown error'}`);
        }
      }

      return { success, errors };
    } catch (error) {
      console.error('Error importing tags from CSV:', error);
      throw error;
    }
  }
}

export const enhancedTagService = EnhancedTagService.getInstance();
